import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { useAgentClientContext } from '../context/AgentContextProvider';
import {
  AgentStreamDisconnectedError,
  getConversationMessages,
  getConversations,
  getSystemModules,
  sendAgentMessage,
} from './_requests';
import type {
  AgentChatResponseDto,
  AgentConversationSummaryDto,
  AgentMessageDto,
  AgentPendingActionDto,
} from './_models';
import { agentKeys } from './queryKeys';

const AI_MODULE_NAME = 'AI_MODULE';
const SYSTEM_MODULES_QUERY_KEY = ['system-modules'];

export const useAiModuleEnabled = (): boolean => {
  const { data } = useQuery({
    queryKey: SYSTEM_MODULES_QUERY_KEY,
    queryFn: getSystemModules,
    meta: { suppressGlobalError: true },
  });
  return !!data?.data?.modules?.some((m) => m.name === AI_MODULE_NAME && m.active);
};

const NEW_CONVERSATION = 'new';

export type UseAgentChatResult = {
  messages: AgentMessageDto[];
  isRunning: boolean;
  isHistoryLoading: boolean;
  isConversationsLoading: boolean;
  historyFailed: boolean;
  sendError: string | null;
  activeToolNames?: string[];
  canRetrySend?: boolean;
  conversationId: string | undefined;
  resumedPendingAction: AgentPendingActionDto | null;
  conversations: AgentConversationSummaryDto[];
  sendMessage: (text: string) => Promise<void>;
  retry: () => void;
  startNewConversation: () => void;
  openConversation: (conversationId: string) => void;
};

export const useAgentChat = (): UseAgentChatResult => {
  const queryClient = useQueryClient();
  const getClientContext = useAgentClientContext();
  const [override, setOverride] = useState<string>(NEW_CONVERSATION);
  const [inFlightText, setInFlightText] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [activeToolNames, setActiveToolNames] = useState<string[]>([]);
  const [canRetrySend, setCanRetrySend] = useState(true);
  const [summaryActionCleared, setSummaryActionCleared] = useState(false);

  const conversationsQuery = useQuery({
    queryKey: agentKeys.conversations(),
    queryFn: getConversations,
    meta: { suppressGlobalError: true },
  });

  const conversations = conversationsQuery.data?.content ?? [];
  const conversationId = override === NEW_CONVERSATION ? undefined : override;

  const followedSummary = conversations.find((c) => c.id === override);
  const resumedPendingAction = summaryActionCleared ? null : (followedSummary?.pending_action ?? null);

  const messagesQuery = useQuery({
    queryKey: agentKeys.messages(conversationId ?? ''),
    queryFn: () => getConversationMessages(conversationId!),
    enabled: !!conversationId,
    // A first-mount refetch would overwrite the exchange appended to this cache.
    staleTime: Infinity,
    meta: { suppressGlobalError: true },
  });

  const mutation = useMutation({
    mutationFn: (text: string) =>
      sendAgentMessage(
        {
          message: text,
          conversation_id: conversationId,
          client_context: getClientContext(),
        },
        (event) => {
          if (event.type === 'TOOL_CALL') {
            setActiveToolNames((current) => [...current, event.tool_name]);
          } else if (event.type === 'TOOL_RESULT') {
            setActiveToolNames((current) => {
              const index = current.indexOf(event.tool_name);
              return index < 0 ? current : current.filter((_, position) => position !== index);
            });
          }
        },
      ),
    meta: { suppressGlobalError: true },
    onMutate: (text) => {
      setInFlightText(text);
      setSendError(null);
      setActiveToolNames([]);
      setCanRetrySend(true);
    },
    onSuccess: (response: AgentChatResponseDto, text) => {
      const exchange: AgentMessageDto[] = [
        {
          id: crypto.randomUUID(),
          role: 'USER',
          content: text,
          created_at: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          role: 'ASSISTANT',
          content: response.answer,
          created_at: new Date().toISOString(),
          requires_confirmation: response.requires_confirmation,
          pending_action: response.pending_action,
          escalate: response.escalate,
        },
      ];
      queryClient.setQueryData<AgentMessageDto[]>(agentKeys.messages(response.conversation_id), (old) => [
        ...(old ?? []),
        ...exchange,
      ]);
      setOverride(response.conversation_id);
      setSummaryActionCleared(true);
      setInFlightText(null);
      void queryClient.invalidateQueries({ queryKey: agentKeys.conversations() });
    },
    onError: (error) => {
      if (error instanceof AgentStreamDisconnectedError && conversationId) {
        setInFlightText(null);
        setCanRetrySend(false);
        setSendError(
          'A conexão foi interrompida, mas a resposta continua sendo processada. Reabra esta conversa em instantes.',
        );
        void queryClient.invalidateQueries({ queryKey: agentKeys.messages(conversationId) });
        void queryClient.invalidateQueries({ queryKey: agentKeys.conversations() });
        return;
      }
      setSendError(getErrorMessage(error) ?? 'Não foi possível enviar a mensagem.');
    },
    onSettled: () => setActiveToolNames([]),
  });

  const history = useMemo(() => messagesQuery.data ?? [], [messagesQuery.data]);

  const messages = useMemo<AgentMessageDto[]>(() => {
    if (inFlightText === null) return history;
    return [
      ...history,
      {
        id: 'optimistic-user',
        role: 'USER',
        content: inFlightText,
        created_at: new Date().toISOString(),
      },
    ];
  }, [history, inFlightText]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || mutation.isPending) return;
      await mutation.mutateAsync(trimmed).catch(() => undefined);
    },
    [mutation],
  );

  const retry = useCallback(() => {
    if (canRetrySend && inFlightText !== null && !mutation.isPending) {
      mutation.mutateAsync(inFlightText).catch(() => undefined);
    }
  }, [canRetrySend, inFlightText, mutation]);

  const startNewConversation = useCallback(() => {
    // A late send completion would otherwise re-pin the conversation after reset.
    if (mutation.isPending) return;
    setOverride(NEW_CONVERSATION);
    setSummaryActionCleared(false);
    setInFlightText(null);
    setSendError(null);
    setActiveToolNames([]);
    setCanRetrySend(true);
  }, [mutation]);

  const openConversation = useCallback(
    (id: string) => {
      if (mutation.isPending) return;
      setOverride(id);
      setSummaryActionCleared(false);
      setInFlightText(null);
      setSendError(null);
      setActiveToolNames([]);
      setCanRetrySend(true);
    },
    [mutation],
  );

  return {
    messages,
    isRunning: mutation.isPending,
    isHistoryLoading: !!conversationId && messagesQuery.isPending,
    isConversationsLoading: conversationsQuery.isPending,
    historyFailed: conversationsQuery.isError || messagesQuery.isError,
    sendError,
    activeToolNames,
    canRetrySend,
    conversationId,
    resumedPendingAction,
    conversations,
    sendMessage,
    retry,
    startNewConversation,
    openConversation,
  };
};
